// file for selectors that depend on multiple different states to prevent circular dependencies
import { isAfter } from 'date-fns';
import { shallowEqual } from 'react-redux';
import { createSelector } from 'reselect';
import {
  AllDocumentTypes,
  Difference,
  DifferenceTypeNames,
  DocumentTypes,
  GridTypes,
  InspectionSettings,
  StoreState,
} from 'types';
import { getFocusedDifferenceId, getInspectionSettings } from './inspection/inspectionSelectors';
import { getDifferences } from './request/differences/selectors';
import { getGroups } from './request/difference-group/selectors';
import { getDifferenceStatusFilters } from './request/inspections/selectors';
import { getHideDisplayPanels } from './app/appSelectors';
import { buildDifferenceGrids } from './differenceGrid';

type GroupedDifferencesSelector = ReturnType<typeof createGroupedDifferencesSelector>;

const resolveGroupOrDifferenceId = (id: Difference | string): string => {
  if (typeof id === 'string') {
    return id;
  }
  return id?.id ?? '';
};

const createGroupedDifferencesSelector = (groupOrDifferenceId: string) =>
  createSelector(
    [getGroups, getDifferences, getDifferenceStatusFilters],
    (groups, differences, statusFilters): Difference[] => {
      let ids = [groupOrDifferenceId];
      if (groups[groupOrDifferenceId]) {
        ids = groups[groupOrDifferenceId].differenceList;
      }
      const groupedDifferences = differences.filter(
        (diff: Difference) => ids?.includes(diff.id) && (!statusFilters.smartDiscard || !diff.smartDiscard),
      );

      // Sorted by reading order
      return groupedDifferences.sort((a, b) => ids.indexOf(a.id) - ids.indexOf(b.id));
    },
    // Return the previous grouped differences array reference when the array elements are unchanged, so
    // consumers (e.g. DifferenceElement) don't re-render on unrelated `differences` mutations.
    // Keeps the churn isolated to the group that changed, not all differences.
    { memoizeOptions: { resultEqualityCheck: shallowEqual } },
  );

const groupedDifferencesSelectorCache = new Map<string, GroupedDifferencesSelector>();

export const clearGroupedDifferencesSelectorCache = (): void => {
  groupedDifferencesSelectorCache.clear();
};

export const getGroupedDifferences = (id: Difference | string): GroupedDifferencesSelector => {
  const resolvedId = resolveGroupOrDifferenceId(id);
  const cached = groupedDifferencesSelectorCache.get(resolvedId);
  if (cached) {
    return cached;
  }

  const selector = createGroupedDifferencesSelector(resolvedId);
  groupedDifferencesSelectorCache.set(resolvedId, selector);
  return selector;
};

/**
 * A difference's group members in reading order, falling back to the difference on its own when its
 * group isn't in the store.
 *
 * The groups query is loaded separately from the differences that reference it, so a difference can
 * carry a group id the store hasn't fetched yet — a re-inspect creates groups server-side and
 * returns differences pointing at them. `getGroupedDifferences` resolves an unknown group id to an
 * empty list, which reads to callers as "this difference doesn't exist" and silently drops it.
 */
export const getDifferenceGroupMembers = (state: StoreState, difference: Difference): Difference[] => {
  const members = getGroupedDifferences(difference.groupId || difference.id)(state);
  return members.length > 0 ? members : [difference];
};

const getUpdatedAt = (state: StoreState) => state.inspection.updatedAt;
export const getInspectionResultsLastUpdated = createSelector(
  [getUpdatedAt, getDifferences],
  (updatedAt, differences) => {
    let lastUpdated = new Date(updatedAt);

    // we check if the differences have updated later than the inspection
    differences.forEach((difference) => {
      const differenceDate = new Date(difference.updatedAt);
      if (isAfter(differenceDate, lastUpdated)) {
        lastUpdated = differenceDate;
      }
    });
    return lastUpdated;
  },
);

export const getFocusedDifference = createSelector(
  [getDifferences, getGroups, getFocusedDifferenceId],
  (differences, groups, focusedDifferenceId) => {
    let diffId = focusedDifferenceId;
    if (diffId && groups[diffId]) {
      diffId = groups[diffId]?.differenceList[0];
    }

    return differences.find((diff) => diff.id === diffId);
  },
);

export const getFocusedDifferenceGrids = createSelector([getFocusedDifference], (difference) => {
  if (
    difference &&
    (difference.displayName === DifferenceTypeNames.Change ||
      (Object.values(GridTypes) as string[]).includes(difference.type))
  ) {
    return buildDifferenceGrids(difference.source?.grid || [], difference.target?.grid || []);
  }

  return { masterGrid: [], sampleGrid: [] };
});

export const getAnnotationsLoaded = (state: StoreState) => state.docManager.annotationsLoaded;
export const getAllAnnotationsLoaded = createSelector(
  [getAnnotationsLoaded, getInspectionSettings, getHideDisplayPanels],
  (annotLoaded: AllDocumentTypes[], inspectionSettings: InspectionSettings, hideDisplayPanels) => {
    if (inspectionSettings.singleFile) {
      return annotLoaded.includes(DocumentTypes.target);
    }
    if (hideDisplayPanels.source) {
      return annotLoaded.includes(DocumentTypes.target);
    } else if (hideDisplayPanels.target) {
      return annotLoaded.includes(DocumentTypes.source);
    } else {
      return annotLoaded.includes(DocumentTypes.source) && annotLoaded.includes(DocumentTypes.target);
    }
  },
);
